import {
    EPlankKind,
    IBalusterGeometry,
    IPlankGeometry,
    IRailingGeometry,
    IStairConfig,
    IStepGeometry,
    IStringerBlock,
    IStringerGeometry,
    IStringerType,
    ISupportPosts,
    IVec3,
} from '@stair-craft/common/util';

import { PLANK_HEIGHT, PLANK_WIDTH } from '../../constants';
import { IBoardBand, boardBand } from '../shared-board-band/board-band';
import { kosourRunAxis } from '../shared-board-profile/board-profile';
import { IPlanPoint, planAdd, planDot, planLength, planSub, planUnit } from '../shared-plan-geometry/plan-geometry';
import { riserSpanY } from '../shared-riser-board/riser-board';
import { insideOutline, treadFootprint } from '../shared-tread-footprint/tread-footprint';

/** Heights within this much count as one level (mm). */
const FLAT: number = 0.5;
/** The shortest plank worth cutting (mm). */
const MIN_PLANK: number = 1;
/** Sampling step along a level run when looking for steps in a plank's way (mm). */
const SAMPLE_STEP: number = 2;

/** A post that cuts a plank, with its own width: a turn's support post is wider than a railing post. */
interface ISizedPost {
    readonly post: IBalusterGeometry;
    readonly width: number;
}

/** A stretch of a run in plan millimetres from its start. */
interface ISpan {
    readonly from: number;
    readonly to: number;
}

/**
 * One planked run of a railing — a handrail segment — measured by `s`, the plan distance from its
 * start. A plank is a bar along the run, cut square to its axis, so on a sloped run it is
 * `secant` times thicker measured upright, and its upper and lower faces are shifted along the
 * run against each other by `shift` per mm of bar height.
 */
interface IPlankedRun {
    readonly from: IVec3;
    readonly to: IVec3;
    readonly plan: number;
    /** Rise of the handrail per plan mm. */
    readonly slope: number;
    readonly secant: number;
    /** Plan shift of a face half a bar height off the centre line, per mm: the signed sine of the tilt. */
    readonly shift: number;
    /** Handrail centre height minus the underside of what the balusters stand on, at the run's middle. */
    readonly drop: number;
    /**
     * The тетива boards under the run, or null: then a bottom plank lies on the walking surface of a
     * level run. Over a board the balusters go straight into its top, and only the top plank runs
     * under the handrail.
     */
    readonly boards: readonly IStringerGeometry[] | null;
}

/** A planked run and the stretches of it its planks cover. */
interface ICutRun {
    readonly run: IPlankedRun;
    readonly spans: ISpan[];
}

/** Where a plan point falls on a plan segment: the share along it and the plan distance off it. */
function project(from: IPlanPoint, to: IPlanPoint, point: IPlanPoint): { t: number; off: number } {
    const along: IPlanPoint = planSub(to, from);
    const t: number = planDot(planSub(point, from), along) / Math.max(1e-9, planDot(along, along));
    return { t, off: planLength(planSub(planAdd(from, along, t), point)) };
}

function lerp(from: IVec3, to: IVec3, t: number): IVec3 {
    return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t, z: from.z + (to.z - from.z) * t };
}

/** The top of a turn block over a plan point inside its outline, or null outside it. */
function blockTopAt(board: IStringerGeometry, point: IPlanPoint): number | null {
    const block: IStringerBlock | undefined = board.block;
    if (!block || !insideOutline(block.outline, point)) {
        return null;
    }
    const { t } = project(block.from, block.to, point);
    const share: number = Math.min(1, Math.max(0, t));
    return block.topFrom + (block.topTo - block.topFrom) * share;
}

/**
 * Whether a plan point lies over a тетива profile board, across its thickness: a left board grows
 * inward from its line toward the larger coordinate, a right one toward the smaller, the protrusion
 * reaching back past the line. A U stair without a gap lays two boards side by side on one line.
 */
function overProfileBoard(board: IStringerGeometry, point: IPlanPoint): boolean {
    const { runAlongX, fixedCoord } = kosourRunAxis(board.points);
    const inward: number = ((runAlongX ? point.z : point.x) - fixedCoord) * (board.side === 'left' ? 1 : -1);
    const band: IBoardBand = boardBand('tetiva');
    return inward >= band.near - FLAT && inward <= band.far + FLAT;
}

/**
 * The top of the тетива under a handrail point: the highest board top — a profile board or a turn
 * block under it — below the handrail. Null when no board runs there.
 */
function boardTopAt(boards: readonly IStringerGeometry[], point: IVec3): number | null {
    let best: number | null = null;
    const take: (top: number | null) => void = (top: number | null): void => {
        if (top !== null && top < point.y && top > (best ?? -Infinity)) {
            best = top;
        }
    };
    for (const board of boards) {
        take(blockTopAt(board, point));
        for (let i: number = 0; !board.block && overProfileBoard(board, point) && i < board.points.length - 1; i++) {
            const { t } = project(board.points[i], board.points[i + 1], point);
            take(t >= 0 && t <= 1 ? lerp(board.points[i], board.points[i + 1], t).y : null);
        }
    }
    return best;
}

/**
 * The planked runs of one railing: each handrail segment longer than a post with something under
 * its middle for the balusters — a тетива board, or the walking surface of a level run a rail
 * height below the handrail. A sloped run with no board under it gets no planks: over a kosour the
 * balusters stand on the treads.
 */
function plankedRuns(railing: IRailingGeometry, railHeight: number, boards: readonly IStringerGeometry[] | null): IPlankedRun[] {
    const points: IVec3[] = railing.handrail.points;
    const runs: IPlankedRun[] = [];
    for (let i: number = 0; i < points.length - 1; i++) {
        const from: IVec3 = points[i];
        const to: IVec3 = points[i + 1];
        const plan: number = planLength(planSub(to, from));
        const middle: IVec3 = lerp(from, to, 0.5);
        const board: number | null = boards ? boardTopAt(boards, middle) : null;
        const level: boolean = Math.abs(to.y - from.y) < FLAT;
        if (plan > railing.postWidth && (board !== null || level)) {
            const slope: number = (to.y - from.y) / plan;
            const secant: number = Math.hypot(1, slope);
            const drop: number = board === null ? railHeight : middle.y - board;
            runs.push({ from, to, plan, slope, secant, drop, shift: slope / secant, boards: board === null ? null : boards });
        }
    }
    return runs;
}

/**
 * Where a level run's bottom plank would run into a step standing on its walking surface — the
 * next flight's first riser and tread at the inner edge of a U landing: sampled along the run, the
 * plank's two side edges included, against every step whose height overlaps the plank's.
 */
function stepBlocks(run: IPlankedRun, steps: readonly IStepGeometry[], stringerType: IStringerType): ISpan[] {
    const surface: number = run.from.y - run.drop;
    const rising: IStepGeometry[] = steps.filter(
        (step: IStepGeometry) => step.position.y > surface + FLAT && riserSpanY(step).yBot < surface + PLANK_HEIGHT - FLAT
    );
    if (run.slope !== 0 || rising.length === 0) {
        return [];
    }
    const outlines: IPlanPoint[][] = rising.map((step: IStepGeometry) => treadFootprint(step, stringerType));
    const across: IPlanPoint = planAdd({ x: 0, z: 0 }, planUnit(planSub(run.to, run.from)), PLANK_WIDTH / 2);
    const blocks: ISpan[] = [];
    for (let s: number = 0; s <= run.plan; s += SAMPLE_STEP) {
        const centre: IPlanPoint = lerp(run.from, run.to, s / run.plan);
        const edges: IPlanPoint[] = [
            centre,
            planAdd(centre, { x: -across.z, z: across.x }),
            planAdd(centre, { x: across.z, z: -across.x }),
        ];
        if (outlines.some((outline: IPlanPoint[]) => edges.some((point: IPlanPoint) => insideOutline(outline, point)))) {
            blocks.push({ from: s - SAMPLE_STEP, to: s + SAMPLE_STEP });
        }
    }
    return blocks;
}

/**
 * The stretches of a run its planks cover: the run less every post standing on it (a tilted
 * plank's square-cut corner kept out of the post too), less the steps rising from a level run's
 * surface, and less half a plank at a post-less start that meets the previous run — that plank
 * runs to the corner, this one butts against its side.
 */
function plankSpans(run: IPlankedRun, posts: readonly ISizedPost[], joinedStart: boolean, steps: ISpan[]): ISpan[] {
    const corner: number = (PLANK_HEIGHT / 2) * Math.abs(run.shift);
    const blocked: ISpan[] = [...steps];
    blocked.push(
        ...posts.flatMap(({ post, width }: ISizedPost) => {
            const reach: number = width / 2 + corner;
            const { t, off } = project(run.from, run.to, post.position);
            const at: number = t * run.plan;
            // A post beside the rail line still blocks the plank where their squares overlap — and only at the plank's height:
            // an upper-floor post over a flight stands on the slab, high above the flight's planks.
            const beside: boolean = off < (width + PLANK_WIDTH) / 2;
            const rail: number = run.from.y + run.slope * at;
            const level: boolean = post.position.y < rail && post.position.y + post.height > rail - run.drop;
            const along: boolean = at > -reach && at < run.plan + reach;
            return beside && level && along ? [{ from: at - reach, to: at + reach }] : [];
        })
    );
    if (joinedStart && !blocked.some((span: ISpan) => span.from <= 0)) {
        blocked.push({ from: -Infinity, to: PLANK_WIDTH / 2 + corner });
    }
    blocked.sort((a: ISpan, b: ISpan) => a.from - b.from);
    const spans: ISpan[] = [];
    let start: number = 0;
    for (const span of blocked) {
        spans.push({ from: start, to: Math.min(span.from, run.plan) });
        start = Math.max(start, span.to);
    }
    spans.push({ from: start, to: run.plan });
    return spans.filter((span: ISpan) => span.to - span.from > MIN_PLANK);
}

function plank(kind: EPlankKind, run: IPlankedRun, span: ISpan, lift: number): IPlankGeometry {
    const at: (s: number) => IVec3 = (s: number): IVec3 => {
        const point: IVec3 = lerp(run.from, run.to, s / run.plan);
        return { ...point, y: point.y + lift };
    };
    return { kind, width: PLANK_WIDTH, height: PLANK_HEIGHT, points: [at(span.from), at(span.to)] };
}

/** Whether a baluster stands on the rail line of a run, within one of its planks. */
function onPlank(baluster: IBalusterGeometry, item: ICutRun): boolean {
    const { t, off } = project(item.run.from, item.run.to, baluster.position);
    const s: number = t * item.run.plan;
    return off <= FLAT && item.spans.some((span: ISpan) => s >= span.from && s <= span.to);
}

/**
 * Where a baluster's foot goes into the тетива under it: the lower of the board tops under its two
 * edges along the run, so no edge hangs over the board. Where neither edge finds a board — a
 * baluster past a board's end — it keeps the run's drop at the middle.
 */
function footInBoards(run: IPlankedRun, boards: readonly IStringerGeometry[], s: number, half: number): number {
    const tops: number[] = [s - half, s + half]
        .map((at: number) => boardTopAt(boards, lerp(run.from, run.to, at / run.plan)))
        .filter((top: number | null): top is number => top !== null);
    return tops.length > 0 ? Math.min(...tops) : run.from.y + run.slope * s - run.drop;
}

/**
 * Sets a baluster standing on a planked run onto its bottom plank, or into the тетива, and under
 * the handrail. The baluster's ends are square, so on a slope each touches at one edge: the foot
 * rests on the highest point of the bottom plank's upper face under it — taken where that face
 * really is, short of a square-cut plank end — and the top meets the lowest point of the
 * handrail's lower face over it.
 */
function seatOnPlanks(baluster: IBalusterGeometry, run: IPlankedRun, spans: readonly ISpan[], railing: IRailingGeometry): boolean {
    const { t, off } = project(run.from, run.to, baluster.position);
    const s: number = t * run.plan;
    const span: ISpan | undefined = spans.find((item: ISpan) => s >= item.from && s <= item.to);
    if (off > FLAT || span === undefined) {
        return false;
    }
    const half: number = railing.balusterWidth / 2;
    const faceShift: number = (PLANK_HEIGHT / 2) * run.shift;
    const rail: (at: number) => number = (at: number): number => run.from.y + run.slope * at;
    // The upper face of the bottom plank lies a half height uphill-back of its centre line, the handrail's lower face downhill-forward.
    const upper: ISpan = { from: Math.max(s - half, span.from - faceShift), to: Math.min(s + half, span.to - faceShift) };
    const lower: ISpan = { from: s - half, to: s + half };
    // On the plank the foot rests on its highest point; into the тетива it goes down to the board's lowest top under it, leaving no gap.
    const base: number = run.boards
        ? footInBoards(run, run.boards, s, half)
        : Math.max(rail(upper.from), rail(upper.to)) - run.drop + PLANK_HEIGHT;
    const top: number = Math.min(rail(lower.from), rail(lower.to)) - (run.secant * railing.handrail.height) / 2;
    baluster.position.y = base;
    baluster.height = Math.max(0, top - base);
    return true;
}

/**
 * Every post of every railing, in the frame of one railing: a stair railing's planks end at the
 * upper-floor railing's corner post too, and that one stands in room millimetres — the stair's
 * own plus `placement`.
 */
function postsInFrameOf(railing: IRailingGeometry, railings: readonly IRailingGeometry[], placement: IPlanPoint): IBalusterGeometry[] {
    const upper: boolean = railing.kind === 'upper';
    return railings.flatMap((other: IRailingGeometry) => {
        if ((other.kind === 'upper') === upper) {
            return other.posts;
        }
        const sign: number = upper ? 1 : -1;
        return other.posts.map((post: IBalusterGeometry) => ({
            ...post,
            position: { ...post.position, x: post.position.x + sign * placement.x, z: post.position.z + sign * placement.z },
        }));
    });
}

/**
 * One post where a stair railing meets the opening railing: an upper-floor corner post that a stair
 * post overlaps — in plan and in height — is dropped, and the stair post takes its place: the upper
 * handrail ends against it.
 */
function shareCornerPosts(railings: readonly IRailingGeometry[], placement: IPlanPoint): void {
    const stairPosts: IBalusterGeometry[] = railings
        .filter((railing: IRailingGeometry) => railing.kind !== 'upper')
        .flatMap((railing: IRailingGeometry) => railing.posts)
        .map((post: IBalusterGeometry) => ({ ...post, position: { ...post.position, ...planAdd(post.position, placement) } }));
    for (const upper of railings.filter((railing: IRailingGeometry) => railing.kind === 'upper')) {
        const reach: number = upper.postWidth - FLAT;
        const clashes: (post: IBalusterGeometry, stair: IBalusterGeometry) => boolean = (
            post: IBalusterGeometry,
            stair: IBalusterGeometry
        ): boolean =>
            Math.abs(stair.position.x - post.position.x) < reach &&
            Math.abs(stair.position.z - post.position.z) < reach &&
            stair.position.y + stair.height > post.position.y + FLAT;
        upper.posts = upper.posts.filter((post: IBalusterGeometry) => !stairPosts.some((stair: IBalusterGeometry) => clashes(post, stair)));
    }
}

/**
 * A baluster on no planked run — over the winders the handrail bends in short pieces — still goes
 * into the тетива or the turn block under it: its foot drops or rises to the lowest board top under
 * its four corners, its top stays where it was.
 */
function setIntoBoard(baluster: IBalusterGeometry, boards: readonly IStringerGeometry[], half: number): void {
    const top: number = baluster.position.y + baluster.height;
    const tops: number[] = [
        [-half, -half],
        [-half, half],
        [half, -half],
        [half, half],
    ]
        .map(([dx, dz]: number[]) => boardTopAt(boards, { x: baluster.position.x + dx, y: top, z: baluster.position.z + dz }))
        .filter((board: number | null): board is number => board !== null);
    if (tops.length > 0) {
        baluster.position.y = Math.min(...tops);
        baluster.height = Math.max(0, top - baluster.position.y);
    }
}

/** What one railing's planks are cut by and laid over. */
interface IPlankContext {
    readonly railings: readonly IRailingGeometry[];
    readonly steps: readonly IStepGeometry[];
    readonly config: IStairConfig;
    readonly placement: IPlanPoint;
    readonly boards: readonly IStringerGeometry[] | null;
    readonly supports?: ISupportPosts;
}

/** Lays one railing's bottom planks and seats its balusters on them or into the тетива under them. */
function plankRailing(railing: IRailingGeometry, context: IPlankContext): void {
    const upper: boolean = railing.kind === 'upper';
    const boards: readonly IStringerGeometry[] | null = upper ? null : context.boards;
    // The turn's support posts stand in the stair frame and cut a stair railing's planks like its own posts.
    const turnPosts: IBalusterGeometry[] = upper ? [] : (context.supports?.posts ?? []);
    const posts: ISizedPost[] = [
        ...postsInFrameOf(railing, context.railings, context.placement).map((post: IBalusterGeometry) => ({
            post,
            width: railing.postWidth,
        })),
        ...turnPosts.map((post: IBalusterGeometry) => ({ post, width: context.supports?.width ?? 0 })),
    ];
    const runs: IPlankedRun[] = plankedRuns(railing, context.config.railing.height, boards);
    const cutBy: (cutters: readonly ISizedPost[]) => ICutRun[] = (cutters: readonly ISizedPost[]): ICutRun[] =>
        runs.map((run: IPlankedRun) => ({
            run,
            spans: plankSpans(
                run,
                cutters,
                run.from !== railing.handrail.points[0],
                upper ? [] : stepBlocks(run, context.steps, context.config.stringerType)
            ),
        }));
    // A baluster standing on no plank — a filler where a support post took a railing post's place — ends the plank like a post.
    const first: ICutRun[] = cutBy(posts);
    const stray: ISizedPost[] = railing.balusters
        .filter((baluster: IBalusterGeometry) => !first.some((item: ICutRun) => onPlank(baluster, item)))
        .map((post: IBalusterGeometry) => ({ post, width: railing.balusterWidth }));
    const cut: ICutRun[] = cutBy([...posts, ...stray]);
    railing.planks = cut.flatMap((item: ICutRun) =>
        item.spans.flatMap((span: ISpan) => [
            ...(item.run.boards ? [] : [plank(EPlankKind.Bottom, item.run, span, PLANK_HEIGHT / 2 - item.run.drop)]),
        ])
    );
    for (const baluster of railing.balusters) {
        const seated: boolean = cut.some((item: ICutRun) => seatOnPlanks(baluster, item.run, item.spans, railing));
        if (!seated && boards) {
            setIntoBoard(baluster, boards, railing.balusterWidth / 2);
        }
    }
}

/**
 * Gives every railing its bottom planks between posts (SC-CALC-092): on the walking surface of a
 * level run with no тетива under it, from post face to post face. Over a тетива — on the flights,
 * the winders and the landing edge — the balusters go into its top; elsewhere they stand on the
 * bottom plank; all of them reach the handrail. No top plank: under the handrail it doubled the
 * rail on some runs and hung in short pieces over the winders. On sloped kosour runs and the
 * spiral the balusters stand on the treads and get no planks.
 */
export function addRailingPlanks(
    railings: readonly IRailingGeometry[],
    stringers: readonly IStringerGeometry[],
    steps: readonly IStepGeometry[],
    config: IStairConfig,
    placement: IPlanPoint,
    supports?: ISupportPosts
): void {
    if (config.type === 'spiral') {
        return;
    }
    shareCornerPosts(railings, placement);
    const boards: IStringerGeometry[] | null =
        config.stringerType === 'tetiva'
            ? stringers.filter(
                  (stringer: IStringerGeometry) =>
                      (stringer.bottom !== undefined || stringer.block !== undefined) && stringer.side !== 'center'
              )
            : null;
    for (const railing of railings) {
        plankRailing(railing, { railings, steps, config, placement, boards, supports });
    }
}
